import { useState } from "react";
import { X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useErrorBrain } from "@/contexts/ErrorBrainContext";
import { API } from "@/api";
import axios from "axios";

export default function ErrorToast() {
  const ctx = useErrorBrain();
  if (!ctx) return null;
  const { toast, clearToast } = ctx;

  return (
    <AnimatePresence>
      {toast && (
        <ToastCard
          key="error-toast"
          toast={toast}
          onDismiss={clearToast}
        />
      )}
    </AnimatePresence>
  );
}

function ToastCard({ toast, onDismiss }) {
  const [reported, setReported] = useState(false);
  const [reporting, setReporting] = useState(false);

  const handleReport = async () => {
    if (reported || reporting) return;
    setReporting(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/error-brain/report`, {
        error_code: toast.errorCode,
        fingerprint: toast.fingerprint,
        endpoint: toast.endpoint,
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setReported(true);
    } catch {
      setReported(true);
    }
    setReporting(false);
  };

  return (
    <motion.div
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 100, opacity: 0 }}
      transition={{ type: "spring", damping: 25, stiffness: 350 }}
      className="fixed bottom-20 left-3 right-3 z-[9999] max-w-md mx-auto"
      data-testid="error-toast"
    >
      <div className="bg-white rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.12)] border border-slate-100 p-4 relative">
        {/* Dismiss X */}
        <button
          onClick={onDismiss}
          className="absolute top-3 right-3 w-6 h-6 flex items-center justify-center rounded-full hover:bg-slate-100 transition-colors"
          data-testid="error-toast-dismiss"
          aria-label="Dismiss"
        >
          <X className="w-3.5 h-3.5 text-slate-400" />
        </button>

        {/* Title */}
        <p className="text-[15px] font-semibold text-slate-800 pr-6" data-testid="error-toast-title">
          {toast.title}
        </p>

        {/* Message */}
        <p className="text-[13px] text-slate-500 mt-1 leading-snug" data-testid="error-toast-message">
          {toast.message}
        </p>

        {/* Action button */}
        <button
          onClick={() => { toast.onAction?.(); onDismiss(); }}
          className="mt-3 w-full py-2.5 rounded-xl text-[13px] font-semibold text-white transition-all active:scale-[0.97]"
          style={{ backgroundColor: "var(--theme-primary, #e8723a)" }}
          data-testid="error-toast-action"
        >
          {toast.action}
        </button>

        {/* Report link + ref code */}
        <div className="flex items-center justify-between mt-2.5">
          {toast.showReport ? (
            reported ? (
              <span className="text-[11px] text-emerald-500 font-medium" data-testid="error-toast-reported">
                Thanks — we're on it.
              </span>
            ) : (
              <button
                onClick={handleReport}
                disabled={reporting}
                className="text-[11px] text-slate-400 hover:text-slate-600 transition-colors underline underline-offset-2"
                data-testid="error-toast-report"
              >
                {reporting ? "Reporting..." : "Report this issue"}
              </button>
            )
          ) : <span />}
          <span className="text-[10px] text-slate-300 font-mono" data-testid="error-toast-code">
            ref: {toast.errorCode}
          </span>
        </div>
      </div>
    </motion.div>
  );
}
